import { useContext, useEffect, useState } from "react";
import {
  Button,
  Box,
  Flex,
  Text,
  useMantineTheme,
  ActionIcon,
  Group,
} from "@mantine/core";
import { MdClose } from "react-icons/md";
import { AppContext } from "src/api/AppContext";
import {
  Agreement,
  ClientGroup,
  GenericConnectInput,
  InputMaybe,
  useCheckForSignedAgreementQuery,
  useCreateAgreementRecipientMutation,
  useCreateSignedAgreementMutation,
} from "src/generated/graphql";
import { ModalStages } from "src/utils/constants";
import { RecursivePartial } from "src/utils/utils";
import { currencyFormatter } from "src/utils/formatting";
import { useLoaderOverlay } from "src/api/context/LoaderOverlayContext ";

interface AgreementOverviewProps {
  agreement: RecursivePartial<Agreement> | undefined;
  selectedGroup: RecursivePartial<ClientGroup>;
  isNewAgreement: boolean;
  clientId: string;
  setCurrentModalStage: (e: ModalStages) => void;
  handleModalClose: () => void;
  setSignedAgreementId: (id: string) => void;
}

type ICreateSignedAgreementProps = {
  agreement: GenericConnectInput;
  client: GenericConnectInput;
  clientGroup?: InputMaybe<GenericConnectInput>;
  location: GenericConnectInput;
};

const AgreementOverview = ({
  agreement,
  clientId,
  selectedGroup,
  isNewAgreement,
  setCurrentModalStage,
  handleModalClose,
  setSignedAgreementId,
}: AgreementOverviewProps) => {
  const theme = useMantineTheme();
  const [signedAgreementId, setsignedAgreementId] = useState("");
  const { currentLocation } = useContext(AppContext);
  const [signPolicies, { loading: signPoliciesLoading }] =
    useCreateAgreementRecipientMutation({
      onCompleted: () => {
        handleContinue();
      },
    });

  const [createSignedAgreement, { loading: signedAgreementLoading }] =
    useCreateSignedAgreementMutation({
      onCompleted: (data) => {
        setSignedAgreementId(data.createSignedAgreement.id);
        signPolicies({
          variables: {
            data: {
              client: { id: clientId },
              signedAgreement: { id: data.createSignedAgreement.id },
            },
          },
        });
      },
    });

  const reccurringCost = agreement?.recurringCharges?.reduce(
    (acc, e) => acc + (e?.cost || 0),
    0
  );
  const initialCost = agreement?.initialCharges?.reduce(
    (acc, e) => acc + (e?.cost || 0),
    0
  );

  const handleSignPolicies = () => {
    const payload: ICreateSignedAgreementProps = {
      agreement: { id: agreement?.id! },
      client: { id: clientId },
      location: { id: currentLocation?.id || "" },
    };
    if (selectedGroup && selectedGroup.id) {
      payload["clientGroup"] = { id: selectedGroup.id };
    }
    createSignedAgreement({
      variables: {
        data: payload,
      },
    });
  };

  // check for signed agreement already exists
  useCheckForSignedAgreementQuery({
    variables: {
      agreementId: agreement?.id || "",
      clientId: clientId || "",
      locationId: currentLocation?.id || "",
    },
    skip: !agreement?.id || !clientId || !currentLocation?.id,
    onCompleted(data) {
      setsignedAgreementId(data.checkForSignedAgreement?.id || "");
    },
    fetchPolicy: "network-only",
  });

  const handleSubmit = () => {
    // signedAgreementId already exists then simply navigate to document else sign
    signedAgreementId ? handleContinue() : handleSignPolicies();
  };
  const handleContinue = () => {
    setCurrentModalStage(ModalStages.SIGNPOLICIES);
  };

  const { showOverlayLoader, hideOverlayLoader } = useLoaderOverlay();
  useEffect(() => {
    if (signPoliciesLoading || signedAgreementLoading) {
      showOverlayLoader();
    } else {
      hideOverlayLoader();
    }
  }, [signPoliciesLoading, signedAgreementLoading]);

  return (
    <div
      style={{
        width: "100%",
        minHeight: 500,
        display: "flex",
        flexDirection: "column",
        borderRadius: 4,
      }}
    >
      <Flex
        align={"center"}
        justify={"space-between"}
        style={{
          padding: "10px 20px",
          marginBottom: "10px",
          borderBottom: `2px solid ${theme.colors.light[5]}`,
        }}
      >
        <Text size={22} fw={500}>
          Agreement Overview
        </Text>
        <ActionIcon size="lg" onClick={handleModalClose}>
          <MdClose size={20} />
        </ActionIcon>
      </Flex>

      <Flex
        direction={"column"}
        align={"center"}
        justify={"center"}
        style={{ flex: 1, padding: "10px 20px" }}
      >
        <Box w={120} h={120} bg={theme.colors.light[2]}></Box>
        <Text my={15} size={16} fw={500}>
          {agreement?.name}
        </Text>
        <Text align="center">{agreement?.description}</Text>

        <Flex w={"100%"} direction={"column"} justify={"flex-start"} my={15}>
          <Text size={16} fw={600}>
            Policies
          </Text>
          <Flex direction={"column"} p={10}>
            {agreement?.additionalPolicies?.map((policy: any) => (
              <Text key={policy.id} size={14} fw={400}>
                {policy.name}
              </Text>
            ))}
          </Flex>

          {isNewAgreement ? (
            <>
              <Text size={16} fw={600}>
                Cost
              </Text>
              <Flex direction={"column"} p={10}>
                <Flex justify={"space-between"}>
                  <Text size={14} fw={400}>
                    {agreement?.paymentFrequency}
                  </Text>
                  <Text size={14} fw={400}>
                    {currencyFormatter.format(reccurringCost || 0)}
                  </Text>
                </Flex>
                <Flex justify={"space-between"}>
                  <Text size={14} fw={400}>
                    SignUp Cost
                  </Text>
                  <Text size={14} fw={400}>
                    {currencyFormatter.format(initialCost || 0)}
                  </Text>
                </Flex>
              </Flex>
            </>
          ) : null}

          {isNewAgreement && agreement?.isGroup ? (
            <>
              <Text size={16} fw={600}>
                Group
              </Text>
              <Flex direction={"column"} p={10}>
                <Flex justify={"space-between"}>
                  <Text size={14} fw={400}>
                    {selectedGroup?.name}
                  </Text>
                  <Text size={14} fw={400}>
                    {selectedGroup?.clients?.length}
                  </Text>
                </Flex>
                <Flex justify={"space-between"}>
                  <Text size={14} fw={400}>
                    Cost per member
                  </Text>
                  <Text size={14} fw={400}>
                    {currencyFormatter.format(agreement?.scalingPrice || 0)}
                  </Text>
                </Flex>
              </Flex>
            </>
          ) : null}
        </Flex>

        <Group w={"100%"} spacing={20} align={"center"}>
          <Button
            radius="lg"
            variant="outline"
            onClick={() => setCurrentModalStage(ModalStages.AGREEMENTS)}
            disabled={signPoliciesLoading || signedAgreementLoading}
            style={{
              flex: 1,
              color: theme.colors.secondaryTextColor[6],
            }}
            sx={{
              backgroundColor: theme.colors.secondary[6],
              "&:hover": {
                background: theme.colors.secondary[5],
              },
            }}
          >
            Back
          </Button>
          <Button
            radius="lg"
            variant="filled"
            onClick={handleSubmit}
            disabled={signPoliciesLoading || signedAgreementLoading}
            style={{
              flex: 1,
            }}
            sx={{
              color: theme.colors.primaryTextColor[6],
              "&:hover": {
                backgroundColor: theme.colors.primary[4],
              },
            }}
          >
            Sign Policies
          </Button>
        </Group>
      </Flex>
    </div>
  );
};

export default AgreementOverview;
